import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getValidAccessToken, refreshSession } from "~/auth/authToken";
import sfAuth from "~/auth/SFAuth";
import Config from "~/config";

/**
 * Raw baseQuery against the wsapi GraphQL endpoint. Attaches the logged-in user's FusionAuth
 * `Authorization: Bearer <token>` on every request via the SFAuth-backed auth seam — which also
 * transparently refreshes the token when it is near expiry. The app authenticates to wsapi AS THE
 * USER (per-user PKCE bearer), so wsapi attributes audit/write-back records to the real user.
 *
 * There is deliberately NO `Api <key>` shared-key fallback: a field app must act as the user
 * (audit + least privilege), and the prod chain never sets a worksuite API key.
 *
 * Codegen-generated GraphQL operations are injected onto `baseApi` in task F2 via
 * `injectEndpoints`/`enhanceEndpoints`.
 */
const rawBaseQuery = fetchBaseQuery({
	baseUrl: Config.endpoint,
	prepareHeaders: async (headers) => {
		const token = await getValidAccessToken();
		if (token) {
			headers.set("Authorization", `Bearer ${token}`);
		}
		return headers;
	}
});

/**
 * Codegen request shape. The `typescript-rtk-query` hooks generated into `graphql.ts` call
 * `query: (variables) => ({ document, variables })`; this base query translates that into a single
 * GraphQL POST against `Config.endpoint`. There is deliberately no `graphql-request` dependency —
 * a plain `fetchBaseQuery` POST carries the document + variables.
 */
interface GraphqlRequest {
	document: string | { toString(): string };
	variables?: Record<string, unknown> | void;
}

const isGraphqlRequest = (args: string | FetchArgs | GraphqlRequest): args is GraphqlRequest =>
	typeof args === "object" && "document" in args;

const toFetchArgs = (args: string | FetchArgs | GraphqlRequest): string | FetchArgs => {
	if (isGraphqlRequest(args)) {
		return {
			url: "",
			method: "POST",
			body: { query: String(args.document), variables: args.variables ?? undefined }
		};
	}
	return args;
};

/**
 * Unwraps the GraphQL transport envelope so the codegen-generated hooks see the shape they are
 * typed for. wsapi returns `{ data, errors }`; the `typescript-rtk-query` hooks type each
 * endpoint's result as the INNER GraphQL `data` (e.g. `{ bpfLotCountsByStage: ... }`). Without this
 * unwrap, `result.data` would be the whole envelope and every field access (`data.bpfLotCountsByStage`)
 * would be undefined at runtime.
 *
 * TRANSPORT-level GraphQL errors (schema/validation/auth/resolver failures — the top-level `errors`
 * array) are ALWAYS surfaced as a query error, even when `data` also carries a non-null envelope with
 * the failed field resolved to `null` (e.g. a paging-cap violation like HC0051 resolves the offending
 * field to null alongside a populated `errors` array — every operation in this app selects exactly one
 * top-level field, so a partial-null response is never a legitimate mix of "some fields ok, one
 * failed"). Silently dropping that case previously made a real query failure indistinguishable from a
 * genuinely empty result (P3-F1 — HC0051 read as "no assigned sites" with no console signal at all).
 * MUTATION-level payload errors (HotChocolate UseMutationConvention: `data.<op>.errors`) are a
 * different, unrelated shape — they live INSIDE a successful `data.<op>` object with no top-level
 * `errors` entry, so they are untouched here; consumers read them via the `payload?.errors` guard.
 */
const unwrapGraphqlEnvelope = (
	result: Awaited<ReturnType<typeof rawBaseQuery>>
): Awaited<ReturnType<typeof rawBaseQuery>> => {
	if (result.error || !result.data || typeof result.data !== "object") return result;
	const envelope = result.data as { data?: unknown; errors?: Array<{ message?: string }> };
	if (!("data" in envelope) && !("errors" in envelope)) return result;

	if (Array.isArray(envelope.errors) && envelope.errors.length > 0) {
		return {
			error: {
				status: "CUSTOM_ERROR",
				data: envelope.errors,
				error: envelope.errors[0]?.message ?? "GraphQL error"
			} as FetchBaseQueryError,
			meta: result.meta
		};
	}

	return { data: envelope.data, meta: result.meta };
};

/**
 * 401-retry wrapper (Atlas: GraphQL Base Query with Auth Token Refresh).
 *
 * On a 401 it forces a silent FusionAuth refresh through SFAuth and replays the original request
 * once — `prepareHeaders` re-reads the now-refreshed token from SFAuth on the retry. If the refresh
 * fails the user is sent to login and the original error is surfaced. The successful response is
 * unwrapped from the GraphQL envelope so the generated hooks see their typed shape.
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs | GraphqlRequest, unknown, FetchBaseQueryError> = async (
	rawArgs,
	apiQuery,
	extraOptions
) => {
	const args = toFetchArgs(rawArgs);
	let result = await rawBaseQuery(args, apiQuery, extraOptions);

	if (result.error?.status === 401) {
		const refreshedToken = await refreshSession();
		if (refreshedToken) {
			result = await rawBaseQuery(args, apiQuery, extraOptions);
		} else {
			void sfAuth.redirectToLogin();
		}
	}

	return unwrapGraphqlEnvelope(result);
};

export const baseApi = createApi({
	reducerPath: "api",
	baseQuery: baseQueryWithReauth,
	// "CycleCountData" backs the offline reconcile-on-reconnect step (Atlas: offline read cache) —
	// `NetworkStatusProvider` invalidates it when the service worker broadcasts that a queued
	// `AddBpfLotChange` replay has resolved, so the mission task list / lot-count views refetch and
	// converge on server truth instead of trusting a stale in-memory read.
	tagTypes: ["CycleCountData"],
	endpoints: () => ({})
});
